import React, { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { billingError, billingErrorBody, getConfigAudit, putConfig, validateConfig } from "./billingApi";
import { Button, Card, DummyPill, Field, Messages, TextInput, getIn, inputCls, setIn } from "./ui";

/*
 * Billing > Config: the practice's billing identity (837P loops 2010AA / 2010AB / 2310B / 2310C), clearinghouse
 * settings (Office Ally: DUMMY until the real ids arrive), claim defaults and the payer id map. Saved on the server
 * with validation (NPI check digit, ZIP+4 and no PO Box for the billing / facility addresses, 9-digit TIN,
 * taxonomy format) and an audit row per change. Passwords / keys are never entered here: the server reads SFTP
 * credentials from its own env/file.
 */

const isDummy = (v) => /DUMMY/i.test(String(v || ""));
const FILING = ["", "CI", "BL", "MB", "MC", "HM", "16", "12", "CH", "WC", "ZZ"];

const ConfigTab = ({ config: initial, meta: initialMeta, onSaved, toast }) => {
  const [cfg, setCfg] = useState(initial);
  const [meta, setMeta] = useState(initialMeta || {});
  const [result, setResult] = useState({ errors: [], warnings: [] });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [newPayerKey, setNewPayerKey] = useState("");

  // Take a new config from the parent (after a save elsewhere) -- but never on mount: the first state already
  // came from these props, and a deferred mount effect would wipe what the user typed first.
  const seen = useRef({ initial, initialMeta });
  useEffect(() => {
    if (seen.current.initial === initial && seen.current.initialMeta === initialMeta) return;
    seen.current = { initial, initialMeta };
    setCfg(initial);
    setMeta(initialMeta || {});
    setDirty(false);
  }, [initial, initialMeta]);

  const errs = useMemo(() => {
    const m = {};
    (result.errors || []).forEach((e) => { if (!m[e.field]) m[e.field] = e.message; });
    return m;
  }, [result]);

  const set = (path, value) => { setCfg((c) => setIn(c, path, value)); setDirty(true); };
  const txt = (path, props = {}) => (
    <TextInput value={getIn(cfg, path)} bad={Boolean(errs[path])} onChange={(v) => set(path, v)} {...props} />
  );
  const digits = (path, max) => txt(path, { maxLength: max, inputMode: "numeric", onChange: (v) => set(path, v.replace(/\D/g, "")) });
  const F = ({ label, path, hint, children, className }) => (
    <Field label={label} error={errs[path]} hint={hint} className={className}>{children || txt(path)}</Field>
  );

  const address = (base, { requireZip9 } = {}) => (
    <>
      {F({ label: "Street", path: `${base}.line1`, className: "col-span-2" })}
      {F({ label: "Suite / line 2", path: `${base}.line2` })}
      {F({ label: "City", path: `${base}.city` })}
      {F({ label: "State", path: `${base}.state`, children: (txt(`${base}.state`, { maxLength: 2, onChange: (v) => set(`${base}.state`, v.toUpperCase()) })) })}
      {F({ label: requireZip9 ? "ZIP+4 (9 digits)" : "ZIP", path: `${base}.zip`, children: (digits(`${base}.zip`, 9)) })}
    </>
  );

  const check = async () => {
    setBusy(true);
    try {
      const r = await validateConfig(cfg);
      setResult(r.validation);
      toast(r.validation.errors.length ? "Some fields need fixing" : "Config looks valid", r.validation.errors.length ? "warning" : "success");
    } catch (e) {
      toast(billingError(e, "Could not check"), "error");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await putConfig(cfg, meta.version || 0);
      setCfg(r.config);
      setMeta(r.meta);
      setResult(r.validation);
      setDirty(false);
      onSaved && onSaved(r.config, r.meta);
      toast("Billing config saved", "success");
      if (audit) setAudit((await getConfigAudit()).audit || []);
    } catch (e) {
      const body = billingErrorBody(e);
      if (body?.detail?.errors) setResult(body.detail);
      toast(billingError(e, "Could not save"), "error");
    } finally {
      setBusy(false);
    }
  };

  const showAudit = async () => {
    try {
      setAudit((await getConfigAudit()).audit || []);
    } catch (e) {
      toast(billingError(e, "Could not load the history"), "error");
    }
  };

  if (!cfg) return null;
  const ch = cfg.clearinghouse || {};
  const providers = Object.entries(cfg.providers || {});
  const facilities = Object.entries(cfg.service_facilities || {});
  const payers = Object.entries(cfg.payers || {});
  const dummyField = (k) => (isDummy(ch[k]) ? <DummyPill /> : null);
  const CH = ({ label, k, hint }) => (
    <Field label={<span className="inline-flex items-center gap-1">{label} {dummyField(k)}</span>} error={errs[`clearinghouse.${k}`]} hint={hint}>
      {txt(`clearinghouse.${k}`)}
    </Field>
  );

  const addProvider = () => {
    const id = window.prompt("Athena provider id");
    if (!id || !/^\d+$/.test(id.trim())) return;
    set(`providers.${id.trim()}`, { name: "", first: "", middle: "", last: "", npi: "", taxonomy: "", active: true });
  };
  const removeKey = (section, key) => {
    setCfg((c) => {
      const next = { ...(c[section] || {}) };
      delete next[key];
      return { ...c, [section]: next };
    });
    setDirty(true);
  };
  const addFacility = () => {
    const id = window.prompt("Department id");
    if (!id || !/^\d+$/.test(id.trim())) return;
    set(`service_facilities.${id.trim()}`, { name: "", npi: "", address: { line1: "", line2: "", city: "", state: "TX", zip: "" } });
  };
  const addPayer = () => {
    const key = newPayerKey.trim().toUpperCase();
    if (!key) return;
    set(`payers.${key}`, { payer_id: "", payer_name: "", filing_indicator: "" });
    setNewPayerKey("");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl border border-[#E5E7EB] px-4 py-3">
        <div className="text-[12px] text-[#6B7280]">
          {meta.source === "db" ? `Version ${meta.version}, saved by ${meta.updated_by || "?"} ${meta.updated_at || ""}`
            : meta.source === "seed" ? "Not saved yet: showing the practice's billing file (verified against NPPES) with DUMMY clearinghouse values"
            : "Not saved yet"}
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[12px] text-amber-700">Unsaved changes</span>}
          <Button kind="ghost" onClick={showAudit}>History</Button>
          <Button kind="outline" disabled={busy} onClick={check}>Check</Button>
          <Button disabled={busy || !dirty && meta.source === "db"} onClick={save}>Save</Button>
        </div>
      </div>

      {(result.errors?.length > 0 || result.warnings?.length > 0) && (
        <Card title="Checks"><Messages errors={result.errors} warnings={result.warnings} /></Card>
      )}

      <Card title="Billing provider (837P 2010AA)">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {F({ label: "Legal name", path: "billing_provider.legal_name", className: "col-span-2" })}
          {F({ label: "Doing business as", path: "billing_provider.dba", className: "col-span-2" })}
          {F({ label: "Group NPI", path: "billing_provider.group_npi", children: (digits("billing_provider.group_npi", 10)) })}
          {F({ label: "Group taxonomy", path: "billing_provider.group_taxonomy" })}
          {F({ label: "TIN (9 digits)", path: "billing_provider.tin", children: (digits("billing_provider.tin", 9)) })}
          {F({ label: "Phone", path: "billing_provider.phone" })}
          {address("billing_provider.address", { requireZip9: true })}
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-2">Must be a physical street address (no PO Box) with ZIP+4.</p>
      </Card>

      <Card title="Pay-to address (837P 2010AB)" right={
        <label className="text-[12px] text-[#374151] flex items-center gap-1">
          <input type="checkbox" checked={Boolean(cfg.pay_to?.enabled)} onChange={(e) => set("pay_to.enabled", e.target.checked)} /> Send a pay-to address
        </label>}>
        {cfg.pay_to?.enabled ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{address("pay_to.address")}</div>
        ) : <p className="text-[12px] text-[#6B7280]">Payments go to the billing provider address.</p>}
      </Card>

      <Card title="Service facilities by location (837P 2310C)" right={<Button kind="ghost" onClick={addFacility}>Add location</Button>}>
        <div className="space-y-4">
          {facilities.map(([id]) => (
            <div key={id} className="border border-[#F3F4F6] rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-semibold text-[#374151]">Department {id}</span>
                <button type="button" aria-label={`Remove department ${id}`} className="p-0.5 rounded hover:bg-red-50 text-red-700" onClick={() => removeKey("service_facilities", id)}><CloseIcon sx={{ fontSize: 16 }} /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {F({ label: "Facility name", path: `service_facilities.${id}.name`, className: "col-span-2" })}
                {F({ label: "Facility NPI (optional)", path: `service_facilities.${id}.npi`, children: (digits(`service_facilities.${id}.npi`, 10)) })}
                <div />
                {address(`service_facilities.${id}.address`, { requireZip9: true })}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-2">A location at the billing provider's address is left out of the file, as the guide requires.</p>
      </Card>

      <Card title="Rendering providers (837P 2310B)" right={<Button kind="ghost" onClick={addProvider}>Add provider</Button>}>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] text-[#6B7280] bg-[#F9FAFB]">
                <th className="px-2 py-1.5">Athena id</th><th className="px-2 py-1.5">Display name</th><th className="px-2 py-1.5">First</th>
                <th className="px-2 py-1.5">Middle</th><th className="px-2 py-1.5">Last</th><th className="px-2 py-1.5">NPI</th>
                <th className="px-2 py-1.5">Taxonomy</th><th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {providers.map(([id]) => (
                <tr key={id} className="border-t border-[#F3F4F6] align-top">
                  <td className="px-2 py-1.5 text-[#6B7280]">{id}</td>
                  {["name", "first", "middle", "last"].map((k) => (
                    <td key={k} className="px-2 py-1.5">{txt(`providers.${id}.${k}`)}
                      {errs[`providers.${id}.${k}`] && <span className="block text-[11px] text-red-700">{errs[`providers.${id}.${k}`]}</span>}</td>
                  ))}
                  <td className="px-2 py-1.5">{digits(`providers.${id}.npi`, 10)}
                    {errs[`providers.${id}.npi`] && <span className="block text-[11px] text-red-700">{errs[`providers.${id}.npi`]}</span>}</td>
                  <td className="px-2 py-1.5">{txt(`providers.${id}.taxonomy`, { onChange: (v) => set(`providers.${id}.taxonomy`, v.toUpperCase()) })}
                    {errs[`providers.${id}.taxonomy`] && <span className="block text-[11px] text-red-700">{errs[`providers.${id}.taxonomy`]}</span>}</td>
                  <td className="px-2 py-1.5"><button type="button" aria-label={`Remove provider ${id}`} className="p-0.5 rounded hover:bg-red-50 text-red-700" onClick={() => removeKey("providers", id)}><CloseIcon sx={{ fontSize: 16 }} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Clearinghouse" right={<span className="inline-flex items-center gap-2 text-[12px] text-[#6B7280]">{ch.name}{isDummy(ch.submitter_id) || isDummy(ch.receiver_id) ? <DummyPill /> : null}</span>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {CH({ label: "Clearinghouse name", k: "name" })}
          {CH({ label: "Submitter id (ETIN)", k: "submitter_id" })}
          {CH({ label: "Submitter name", k: "submitter_name" })}
          {CH({ label: "Contact name", k: "submitter_contact_name" })}
          {CH({ label: "Contact phone", k: "submitter_contact_phone" })}
          {CH({ label: "Contact email", k: "submitter_contact_email" })}
          {CH({ label: "Receiver id", k: "receiver_id" })}
          {CH({ label: "Receiver name", k: "receiver_name" })}
          {CH({ label: "ISA05 sender qualifier", k: "isa_sender_qualifier" })}
          {CH({ label: "ISA06 sender id", k: "isa_sender_id" })}
          {CH({ label: "ISA07 receiver qualifier", k: "isa_receiver_qualifier" })}
          {CH({ label: "ISA08 receiver id", k: "isa_receiver_id" })}
          {CH({ label: "GS02 sender code", k: "gs_sender_code" })}
          {CH({ label: "GS03 receiver code", k: "gs_receiver_code" })}
          <Field label="ISA15 usage" error={errs["clearinghouse.usage_indicator"]}>
            <select className={inputCls(errs["clearinghouse.usage_indicator"])} value={ch.usage_indicator || "T"} onChange={(e) => set("clearinghouse.usage_indicator", e.target.value)}>
              <option value="T">T (test)</option><option value="P">P (production)</option>
            </select>
          </Field>
          <Field label="ISA14 acknowledgment">
            <select className={inputCls()} value={ch.ack_requested || "0"} onChange={(e) => set("clearinghouse.ack_requested", e.target.value)}>
              <option value="0">0 (none)</option><option value="1">1 (TA1 requested)</option>
            </select>
          </Field>
          {CH({ label: "File naming", k: "file_naming", hint: "{practice} {yyyymmdd} {batch}" })}
          <Field label="Control number offset" error={errs["clearinghouse.interchange_control_offset"]}>
            {txt("clearinghouse.interchange_control_offset", { onChange: (v) => set("clearinghouse.interchange_control_offset", v.replace(/\D/g, "")) })}
          </Field>
        </div>
        <div className="mt-4 border-t border-[#F3F4F6] pt-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[12px] font-semibold text-[#374151]">SFTP upload (later)</span>
            <label className="text-[12px] text-[#374151] flex items-center gap-1">
              <input type="checkbox" checked={Boolean(ch.sftp?.enabled)} onChange={(e) => set("clearinghouse.sftp.enabled", e.target.checked)} /> Enabled
            </label>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {F({ label: "Host", path: "clearinghouse.sftp.host" })}
            {F({ label: "Port", path: "clearinghouse.sftp.port" })}
            {F({ label: "Username", path: "clearinghouse.sftp.username" })}
            {F({ label: "Remote folder", path: "clearinghouse.sftp.remote_path" })}
          </div>
          <p className="text-[11px] text-[#6B7280] mt-2">Password / key: set via server env/file (never stored here). {ch.sftp?.credential_ref}</p>
        </div>
      </Card>

      <Card title="Claim defaults">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {F({ label: "Office place of service", path: "claim_defaults.pos_office" })}
          {F({ label: "Telehealth place of service", path: "claim_defaults.pos_telehealth", hint: "10 = patient home, 02 = other" })}
          {F({ label: "Telehealth modifier", path: "claim_defaults.telehealth_modifier" })}
          <Field label={<span className="inline-flex items-center gap-1">Fallback payer id {isDummy(cfg.claim_defaults?.default_payer_id) ? <DummyPill /> : null}</span>}>
            {txt("claim_defaults.default_payer_id")}
          </Field>
          <label className="col-span-2 text-[12px] text-[#374151] flex items-center gap-2">
            <input type="checkbox" checked={cfg.claim_defaults?.suggest_modifier_25 !== false}
              onChange={(e) => set("claim_defaults.suggest_modifier_25", e.target.checked)} />
            Suggest modifier 25 on the E/M when a same-day procedure is billed
          </label>
        </div>
      </Card>

      <Card title="Payers (Athena package / plan name → clearinghouse payer id)">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] text-[#6B7280] bg-[#F9FAFB]">
                <th className="px-2 py-1.5">Athena package id or plan name</th><th className="px-2 py-1.5">Payer id</th>
                <th className="px-2 py-1.5">Payer name on the claim</th><th className="px-2 py-1.5">Filing indicator</th><th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {payers.map(([key]) => (
                <tr key={key} className="border-t border-[#F3F4F6]">
                  <td className="px-2 py-1.5 font-mono text-[12px]">{key}</td>
                  <td className="px-2 py-1.5">{txt(`payers.${key}.payer_id`)}
                    {errs[`payers.${key}.payer_id`] && <span className="block text-[11px] text-red-700">{errs[`payers.${key}.payer_id`]}</span>}</td>
                  <td className="px-2 py-1.5">{txt(`payers.${key}.payer_name`)}</td>
                  <td className="px-2 py-1.5">
                    <select className={inputCls()} value={getIn(cfg, `payers.${key}.filing_indicator`) || ""} onChange={(e) => set(`payers.${key}.filing_indicator`, e.target.value)}>
                      {FILING.map((f) => <option key={f} value={f}>{f || "infer from plan"}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5"><button type="button" aria-label={`Remove payer ${key}`} className="p-0.5 rounded hover:bg-red-50 text-red-700" onClick={() => removeKey("payers", key)}><CloseIcon sx={{ fontSize: 16 }} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <TextInput value={newPayerKey} onChange={setNewPayerKey} placeholder="e.g. 11993 or BCBS-TX (PPO)" className="max-w-[260px]" />
          <Button kind="ghost" onClick={addPayer}>Add payer</Button>
        </div>
        <p className="text-[11px] text-[#9CA3AF] mt-2">Unmapped payers get the fallback payer id and a warning on the claim.</p>
      </Card>

      {audit && (
        <Card title="History" right={<Button kind="ghost" onClick={() => setAudit(null)}>Hide</Button>}>
          {audit.length === 0 ? <p className="text-[12px] text-[#6B7280]">No saved changes yet.</p> : (
            <ul className="space-y-2">
              {audit.map((a) => (
                <li key={a.id} className="text-[12px] text-[#374151]">
                  <span className="font-semibold">v{a.version}</span> · {a.changed_by} · {a.changed_at}
                  <ul className="ml-4 text-[#6B7280]">
                    {(a.changes || []).slice(0, 20).map((c, i) => (
                      <li key={i}>{c.path || "(all)"}: {JSON.stringify(c.old)} → {JSON.stringify(c.new)}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
};

export default ConfigTab;
